/**
 * Maintenance Planning API
 * Find cheapest maintenance windows (7am-6pm)
 */

import { apiClient } from "./client";
import type {
  ForecastedMaintenanceWindow,
  MaintenanceScheduleCreate,
  MaintenanceScheduleListResponse,
  MaintenanceScheduleResponse,
  MaintenanceWindow,
  PriceForecastMatrix,
  PriceMatrix,
  RecommendationType,
} from "./types";

// ============================================================================
// HISTORICAL-BASED MAINTENANCE (using actual prices)
// ============================================================================

/**
 * Get price matrix for maintenance planning (historical)
 */
export async function getMaintenanceMatrix(params: {
  startDate?: string;
  days?: number;
  zone?: string;
}): Promise<PriceMatrix> {
  const searchParams = new URLSearchParams();
  if (params.startDate) searchParams.append("start_date", params.startDate);
  if (params.days) searchParams.append("days", params.days.toString());
  if (params.zone) searchParams.append("zone", params.zone);

  const query = searchParams.toString();
  return apiClient.get<PriceMatrix>(
    `/api/v1/maintenance/matrix${query ? `?${query}` : ""}`
  );
}

/**
 * Get cheapest consecutive hour windows for maintenance
 */
export async function getMaintenanceWindows(params: {
  startDate?: string;
  days?: number;
  zone?: string;
  windowHours?: number;
  topN?: number;
}): Promise<MaintenanceWindow[]> {
  const searchParams = new URLSearchParams();
  if (params.startDate) searchParams.append("start_date", params.startDate);
  if (params.days) searchParams.append("days", params.days.toString());
  if (params.zone) searchParams.append("zone", params.zone);
  if (params.windowHours) searchParams.append("window_hours", params.windowHours.toString());
  if (params.topN) searchParams.append("top_n", params.topN.toString());

  const query = searchParams.toString();
  return apiClient.get<MaintenanceWindow[]>(
    `/api/v1/maintenance/windows${query ? `?${query}` : ""}`
  );
}

/**
 * Get top 3 recommended maintenance windows (historical)
 */
export async function getMaintenanceRecommendations(params: {
  startDate?: string;
  days?: number;
  zone?: string;
}): Promise<MaintenanceWindow[]> {
  const searchParams = new URLSearchParams();
  if (params.startDate) searchParams.append("start_date", params.startDate);
  if (params.days) searchParams.append("days", params.days.toString());
  if (params.zone) searchParams.append("zone", params.zone);

  const query = searchParams.toString();
  return apiClient.get<MaintenanceWindow[]>(
    `/api/v1/maintenance/recommendations${query ? `?${query}` : ""}`
  );
}

// ============================================================================
// FORECAST-BASED MAINTENANCE (using predicted prices)
// ============================================================================

/**
 * Get forecasted price matrix for maintenance planning
 */
export async function getMaintenanceForecastMatrix(params: {
  startDate?: string;
  days?: number;
  zone?: string;
  granularity?: "PT60" | "PT15";
}): Promise<PriceForecastMatrix> {
  const searchParams = new URLSearchParams();
  if (params.startDate) searchParams.append("start_date", params.startDate);
  if (params.days) searchParams.append("days", params.days.toString());
  if (params.zone) searchParams.append("zone", params.zone);
  if (params.granularity) searchParams.append("granularity", params.granularity);

  const query = searchParams.toString();
  return apiClient.get<PriceForecastMatrix>(
    `/api/v1/maintenance/forecast/matrix${query ? `?${query}` : ""}`
  );
}

/**
 * Get cheapest consecutive hour/day windows based on price forecast
 *
 * @param windowHours - For hour-based windows (2h, 3h, etc.)
 * @param windowDays - For day-based windows (1, 2, or 3 consecutive days)
 */
export async function getMaintenanceForecastWindows(params: {
  startDate?: string;
  days?: number;
  zone?: string;
  windowHours?: number;
  windowDays?: number; // 1, 2, or 3 for multi-day maintenance
  topN?: number;
  excludeWeekends?: boolean;
  hourMin?: number;
  hourMax?: number;
  // Issue #123 — recommendation modes.
  recommendationType?: RecommendationType;
  plantId?: number; // required when recommendationType is production|combined
}): Promise<ForecastedMaintenanceWindow[]> {
  const searchParams = new URLSearchParams();
  if (params.startDate) searchParams.append("start_date", params.startDate);
  if (params.days) searchParams.append("days", params.days.toString());
  if (params.zone) searchParams.append("zone", params.zone);
  if (params.windowHours) searchParams.append("window_hours", params.windowHours.toString());
  if (params.windowDays) searchParams.append("window_days", params.windowDays.toString());
  if (params.topN) searchParams.append("top_n", params.topN.toString());
  if (params.excludeWeekends) searchParams.append("exclude_weekends", "true");
  if (params.hourMin !== undefined) searchParams.append("hour_min", params.hourMin.toString());
  if (params.hourMax !== undefined) searchParams.append("hour_max", params.hourMax.toString());
  if (params.recommendationType)
    searchParams.append("recommendation_type", params.recommendationType);
  if (params.plantId !== undefined)
    searchParams.append("plant_id", params.plantId.toString());

  const query = searchParams.toString();
  return apiClient.get<ForecastedMaintenanceWindow[]>(
    `/api/v1/maintenance/forecast/windows${query ? `?${query}` : ""}`
  );
}

/**
 * Get top 3 recommended maintenance windows based on forecast
 */
export async function getMaintenanceForecastRecommendations(params: {
  startDate?: string;
  days?: number;
  zone?: string;
  // Issue #123 — recommendation modes. For non-price modes the backend
  // auto-defaults to working hours (07-18) and weekday-only.
  recommendationType?: RecommendationType;
  plantId?: number;
}): Promise<ForecastedMaintenanceWindow[]> {
  const searchParams = new URLSearchParams();
  if (params.startDate) searchParams.append("start_date", params.startDate);
  if (params.days) searchParams.append("days", params.days.toString());
  if (params.zone) searchParams.append("zone", params.zone);
  if (params.recommendationType)
    searchParams.append("recommendation_type", params.recommendationType);
  if (params.plantId !== undefined)
    searchParams.append("plant_id", params.plantId.toString());

  const query = searchParams.toString();
  return apiClient.get<ForecastedMaintenanceWindow[]>(
    `/api/v1/maintenance/forecast/recommendations${query ? `?${query}` : ""}`
  );
}

/**
 * Filter maintenance windows to working hours only (7am-6pm)
 * Per PDF requirements, maintenance should only be scheduled during working hours
 */
export function filterWorkingHours(
  windows: MaintenanceWindow[] | ForecastedMaintenanceWindow[]
): (MaintenanceWindow | ForecastedMaintenanceWindow)[] {
  return windows.filter((window) => {
    // Working hours: 7am (7) to 6pm (18)
    return window.start_hour >= 7 && window.end_hour <= 18;
  });
}

/**
 * Format maintenance window as readable string
 * e.g., "3 hours morning" or "2 hours afternoon"
 */
export function formatMaintenanceWindow(
  window: MaintenanceWindow | ForecastedMaintenanceWindow
): string {
  const hours = window.end_hour - window.start_hour;
  const period = window.start_hour < 12 ? "morning" : "afternoon";
  return `${hours} hour${hours > 1 ? "s" : ""} ${period}`;
}

// ============================================================================
// MAINTENANCE SCHEDULING (#26)
// ============================================================================

/**
 * Create a new maintenance schedule for a plant
 */
export async function createMaintenanceSchedule(
  data: MaintenanceScheduleCreate
): Promise<MaintenanceScheduleResponse> {
  return apiClient.post<MaintenanceScheduleResponse>(
    "/api/v1/maintenance/schedules",
    data
  );
}

/**
 * Get all maintenance schedules for the current user
 */
export async function getMaintenanceSchedules(params?: {
  plantId?: number;
  includeInactive?: boolean;
}): Promise<MaintenanceScheduleListResponse> {
  const searchParams = new URLSearchParams();
  if (params?.plantId)
    searchParams.append("plant_id", params.plantId.toString());
  if (params?.includeInactive)
    searchParams.append("include_inactive", "true");

  const query = searchParams.toString();
  return apiClient.get<MaintenanceScheduleListResponse>(
    `/api/v1/maintenance/schedules${query ? `?${query}` : ""}`
  );
}

/**
 * Update a maintenance schedule (change times, reason, or reactivate)
 */
export async function updateMaintenanceSchedule(
  id: number,
  data: Partial<MaintenanceScheduleCreate> & { is_active?: boolean }
): Promise<MaintenanceScheduleResponse> {
  return apiClient.put<MaintenanceScheduleResponse>(
    `/api/v1/maintenance/schedules/${id}`,
    data
  );
}

/**
 * Delete a maintenance schedule
 */
export async function deleteMaintenanceSchedule(
  id: number
): Promise<void> {
  await apiClient.delete(`/api/v1/maintenance/schedules/${id}`);
}

/**
 * Deactivate (soft-cancel) a maintenance schedule
 */
export async function deactivateMaintenanceSchedule(
  id: number
): Promise<MaintenanceScheduleResponse> {
  return apiClient.post<MaintenanceScheduleResponse>(
    `/api/v1/maintenance/schedules/${id}/deactivate`,
    {}
  );
}
